import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ENUM_LABELS, type RequisitionRow, toOptions } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { Pagination } from '#client/components/ui/Pagination';
import { Segmented } from '#client/components/ui/Segmented';
import { useCan } from '#client/hooks/useCan';
import { useResourceList } from '#client/hooks/useResourceList';
import { useUrlState } from '#client/hooks/useUrlState';
import { RESOURCE, routes } from '#client/lib/routes';
import { requisitionColumns } from './requisition-columns';
import { ButtonLink } from '#client/components/ui/ButtonLink';

const STATUS_FILTERS = [{ value: '', label: 'Open' }, ...toOptions(ENUM_LABELS.requisitionStatus)];

export function RequisitionListPage() {
  const navigate = useNavigate();
  const can = useCan();
  const { params, setParam } = useUrlState();
  const status = params.get('status');
  // Anything still in play. The default view, because a closed requisition is
  // history and an open one is work.
  const shouldShowOpenOnly = params.get('open') !== 'false' && !status;

  const extraQuery = useMemo(
    () => (status ? { status } : shouldShowOpenOnly ? { open: 'true' } : {}),
    [status, shouldShowOpenOnly],
  );
  const requisitions = useResourceList<RequisitionRow>({
    path: RESOURCE.requisitions,
    extraQuery,
  });

  return (
    <Page
      title="Reagent orders"
      subtitle="What has been asked for, what was approved, and what has arrived"
      primaryAction={
        can('requisition.create') ? (
          <ButtonLink variant="primary" size="md" to={routes.requisitionNew}>
            New order
          </ButtonLink>
        ) : undefined
      }
    >
      <Card flush>
        <div className="c-import-filter">
          <Segmented
            label="Show"
            options={STATUS_FILTERS}
            value={status ?? ''}
            onChange={(choice) => setParam('status', choice || undefined)}
          />
        </div>

        <DataTable
          columns={requisitionColumns}
          rows={requisitions.items}
          rowKey={(row) => row.id}
          loading={requisitions.loading}
          skeletonRows={requisitions.limit}
          caption="Reagent orders"
          onRowClick={(row) => void navigate(routes.requisition(row.id))}
          emptyState={
            <EmptyState
              title={status ? 'Nothing in that state' : 'Nothing on order'}
              body="Raise an order when a reagent is running low — the reorder list can start one for you."
              tone="positive"
            />
          }
        />

        <Pagination
          page={requisitions.page}
          limit={requisitions.limit}
          count={requisitions.count}
          onPageChange={requisitions.setPage}
        />
      </Card>
    </Page>
  );
}
